import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import editIcon from "../../assets/icons/edit.svg";
import deleteIcon from "../../assets/icons/delete.svg";

import { useThunk } from "../../hooks/useThunk";
import { useInput } from "../../hooks/use-input";
import {
  createTag,
  getAllTags,
  updateTag,
  deleteTag,
} from "../../services/thunks/tagThunks";
import { validateTag } from "../../utils/validationUtils";
import showToast from "../../utils/toastNotifications";
import { capitalizeEachWord } from "../../utils/formatting";
import { sanitizeHyphens } from "../../utils/formatting";
import { roles } from "../../constants/commonConstants";

import Input from "../../components/Form/Input";
import CustomButton from "../../components/Buttons/CustomButton";
import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import SearchBar from "../../components/common/SearchBar";

const ManageTags = () => {
  const auth = useSelector((state) => state.auth);
  const { role } = auth;
  const isRemixer = role === roles.REMIXER;

  const [editingId, setEditingId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [doCreateTag, isCreatingTag] = useThunk(createTag);
  const [doUpdateTag, isUpdatingTag] = useThunk(updateTag);
  const [doDeleteTag, isDeletingTag] = useThunk(deleteTag);

  const {
    value: tagName,
    handleInputChange: handleTagNameChange,
    handleInputBlur: handleTagNameBlur,
    hasError: tagNameHasError,
    errorMessage: tagNameErrorMessage,
    setErrorMessage,
    reset: resetTagName,
  } = useInput("", (val) => validateTag(val.trim()));

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [rowStates, setRowStates] = useState({});
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);
  const tableUpdateRowRef = useRef(null);
  const tableRemoveRowRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setRowStates({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    if (!isValidForm) {
      return;
    }

    const formattedTagName = capitalizeEachWord(
      sanitizeHyphens(tagName.trim())
    );

    const payload = { tagName: formattedTagName };

    const result = editingId
      ? await doUpdateTag({ token: auth.token, id: editingId, payload })
      : await doCreateTag({ token: auth.token, payload });

    if (result.success) {
      const newId = result.response?.data?.tag?._id || result.id;
      const newIndex = result.response?.data?.index;
      showToast(
        "success",
        editingId
          ? "Tag updated successfully."
          : `Tag ‘${formattedTagName}’ added successfully.`
      );
      resetTagName();
      setEditingId(null);

      const pageSize = 10;
      const pageNumber = Math.floor(newIndex / pageSize) + 1;

      if (pageSetterRef.current) {
        pageSetterRef.current(pageNumber);
      }

      setHighlightedRowId(newId);

      setTimeout(() => {
        setHighlightedRowId(null);
      }, 5000);

      setRefreshTrigger((prev) => prev + 1);
    } else {
      console.error(result.error);
      setErrorMessage(result.error.message);
    }
  };

  const handleDelete = async (id) => {
    const result = await doDeleteTag({ token: auth.token, id });
    if (result.success) {
      const deletedTagName = result.response?.data?.tagName || "Tag";
      showToast("success", `Tag ‘${deletedTagName}’ deleted successfully.`);
      if (tableRemoveRowRef.current) {
        tableRemoveRowRef.current(id);
      }
      const updatedUnavailable = result.response?.data?.updatedUnavailable;
      if (
        updatedUnavailable &&
        updatedUnavailable._id &&
        tableUpdateRowRef.current
      ) {
        tableUpdateRowRef.current(updatedUnavailable._id, {
          usedIn: updatedUnavailable.usedIn,
        });
      }
    } else {
      showToast("error", result.error.message);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleRowSave = React.useCallback(
    async (id, value) => {
      const tagName = capitalizeEachWord(sanitizeHyphens(value.trim()));

      const result = await doUpdateTag({
        token: auth.token,
        id,
        payload: { tagName: tagName },
      });

      if (result.success) {
        showToast("success", `Tag ‘${tagName}’ updated successfully.`);
        setRowStates((prev) => ({
          ...prev,
          [id]: { editing: false, value: undefined },
        }));

        if (tableUpdateRowRef.current) {
          tableUpdateRowRef.current(id, { tagName });
        }

        setHighlightedRowId(id);
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 5000);
      } else {
        console.error(result.error);
        showToast("error", result.error.message);
      }
    },
    [doUpdateTag, auth.token]
  );

  const handleSearch = () => {
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (pageSetterRef.current) {
        pageSetterRef.current(1);
      }
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const isValidForm = tagName && !tagNameHasError;

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: "Tag Name",
        dataIndex: "tagName",
        key: "tagName",
        minWidth: "450px",
        render: (text, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.value ?? text;

          return isEditing ? (
            <Input
              autoFocus
              type="text"
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;
                setRowStates((prev) => ({
                  ...prev,
                  [record._id]: {
                    ...prev[record._id],
                    value: newValue,
                  },
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              inputStyle="bg-transparent border-0 border-b border-[#ccc] rounded-none shadow-none outline-none px-0 pb-[0px] pt-[5px] h-[28px] text-sm [400] leading-[18px] text-[#E2E2E2] font-inter"
              outerContainerStyle="mb-1"
            />
          ) : (
            <div className="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
              {text}
            </div>
          );
        },
      },
      {
        title: "Used In",
        dataIndex: "usedIn",
        key: "usedIn",
        align: "center",
        minWidth: "350px",
        render: (count) => (
          <div className="flex justify-center items-center bg-[#212121] rounded-[10px] w-[50px] h-[25px] leading-[24px] text-[#E2E2E2] text-sm text-center font-[400] font-inter">
            {count || 0}
          </div>
        ),
      },
    ];
    if (!isRemixer) {
      columns.push({
        title: "Action",
        key: "action",
        minWidth: "100px",
        render: (_, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.value ?? record.tagName;

          return (
            <div className="flex gap-[8px] justify-start items-center ">
              {isEditing ? (
                <>
                  <CustomButton
                    onClick={() =>
                      setRowStates((prev) => ({
                        ...prev,
                        [record._id]: { editing: false, value: undefined },
                      }))
                    }
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#CCCCCC] rounded-[15px] text-xs font-normal text-[#CCCCCC] w-[90px] hover:bg-[#333] transition font-inter"
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    onClick={() => handleRowSave(record._id, value)}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#00ffd5] rounded-[15px] text-xs font-normal text-[#00ffd5] w-[90px] hover:bg-[#00ffd53b] hover:text-white transition font-inter"
                    disabled={!value || isUpdatingTag}
                    loading={isUpdatingTag}
                  >
                    Save
                  </CustomButton>
                </>
              ) : record.tagName.toLowerCase() === "unavailable" ? (
                <div className="text-xs text-[#888] italic">Locked</div>
              ) : (
                <>
                  <CustomButton
                    className="flex items-center justify-center gap-2 px-4 py-1 rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#333300] transition font-inter"
                    textColor="text-[#B4C007]"
                    borderColor="border-[#B4C007]"
                    onClick={() => {
                      setRowStates({
                        [record._id]: {
                          editing: true,
                          value: record.tagName,
                        },
                      });
                    }}
                  >
                    <img
                      src={editIcon}
                      alt="edit"
                      className="w-[10px] h-[11px]"
                    />
                    Edit
                  </CustomButton>

                  <CustomButton
                    onClick={() => {
                      setSelectedTag(record);
                      setShowConfirm(true);
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-1 rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#330000] transition font-inter"
                    textColor="text-[#C00707]"
                    borderColor="border-[#C00707]"
                  >
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="w-[10px] h-[12px]"
                    />
                    Delete
                  </CustomButton>
                </>
              )}
            </div>
          );
        },
      });
    }

    return columns;
  }, [rowStates, isRemixer, isUpdatingTag, handleRowSave]);

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Form Section */}
      <div className="bg-[#111111D6] border border-[#505050] p-6 rounded-lg mb-8 ">
        <h2 className="text-xl font-bold font-['Public_Sans'] mb-4 text-[#E3E3E3]">
          {editingId ? "Edit Tag" : "Add New Tag"}
        </h2>
        <div className="flex gap-4 items-end">
          <Input
            label="Tag Name"
            isRequired
            placeholder="Enter Tag Name"
            value={tagName}
            onChange={handleTagNameChange}
            onBlur={handleTagNameBlur}
            errorMessage={tagNameErrorMessage}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle=""
            labelStyle="font-['Public_Sans'] fonts-normal text-[14px] leading-[22px] flex items-center text-[#808080]"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <CustomButton
            useGradientBorder
            rounded="rounded-lg"
            textSize="text-[18px] leading-[24px]"
            padding="py-[10.5px] px-[13.5px]"
            font="font-public-sans"
            border="border-lg"
            type="button"
            width="w-[131px] h-[42px]"
            onClick={handleSubmit}
            disabled={!isValidForm || isCreatingTag}
            loading={isCreatingTag}
          >
            {editingId ? "Update Tag" : "Add Tag"}
          </CustomButton>
        </div>
      </div>

      {/* Search Section */}
      <div className="flex items-center gap-4 mb-6">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setRefreshTrigger={setRefreshTrigger}
          placeholder="Search Tags..."
          containerClass="bg-[#45444433] border border-[#808080] rounded-[5px] px-3 w-full h-[45px]"
          inputClass="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public_Sans'] pr-6"
          buttonClass="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
          onKeyPress={handleKeyPress}
        />
        <CustomButton
          className="rounded-[3.77px] h-[45px] w-[91px] min-w-[91px] text-[11px] uppercase font-['Public_Sans'] font-normal"
          textColor="text-[#51F9EF]"
          borderColor="border-[#51F9EF]"
          onClick={handleSearch}
        >
          Search
        </CustomButton>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={getAllTags}
          refreshTrigger={refreshTrigger}
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => ({
            ...params,
            token: auth.token,
            tagName: searchQuery.trim() || undefined,
          })}
          highlightedRowId={highlightedRowId}
          setPageCallback={(fn) => (pageSetterRef.current = fn)}
          searchQuery={searchQuery}
          updateRowRef={tableUpdateRowRef}
          removeRowRef={tableRemoveRowRef}
        />
      </div>

      <ConfirmDeleteModal
        visible={showConfirm}
        onConfirm={async () => {
          await handleDelete(selectedTag?._id);
          setShowConfirm(false);
          setSelectedTag(null);
        }}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedTag(null);
        }}
        title="Confirm Deletion"
        content={`Are you sure you want to delete the tag "${selectedTag?.tagName}"?`}
        disabled={isDeletingTag}
        loading={isDeletingTag}
      />
    </div>
  );
};

export default ManageTags;
